/**
 * 测试报告 API
 */
import { get, post, del, getPage } from '@/utils/request'

/** 报告步骤执行记录 */
export interface TestReportStep {
  id: number
  report_id: number
  step_name: string
  step_type: number // 1=HTTP/2=等待/3=条件/4=循环/5=断言/6=数据库/7=脚本
  status: number // 1=成功/2=失败/3=错误/4=跳过
  duration_ms: number
  depth: number // 0=顶层/1=子步骤
  sort: number
  result: string // 执行结果详情 JSON 字符串
}

/** 执行产物文件（五期 #3：报告页产物直达） */
export interface ExecutionArtifact {
  name: string
  size: number
  url: string
}

export interface TestReport {
  id: number
  name: string
  task_type: number // 任务类型: 1=接口场景测试/2=UI测试
  run_result: number // 运行结果: 1=成功/2=失败
  run_mode: number // 运行模式: 1=同步/2=异步/3=定时器
  total_steps: number
  success_steps: number
  failed_steps: number
  error_steps: number
  skipped_steps: number
  pass_rate: number
  duration_ms: number
  run_time: string | null
  executor_name: string
  scenario_id: number | null
  project_id: number | null
  plan_id: number | null
  summary: string
  // 四期 L：AI 摘要（按需生成后缓存）
  ai_summary: string
  // 四期 K：分享 token（空=未开启分享）
  share_token: string
  create_time: string | null
  steps?: TestReportStep[]
  // 五期 #3：关联执行与产物清单（详情接口返回，UI 报告专属）
  execution_id?: number
  execution_artifacts?: ExecutionArtifact[]
  // 五期 #5：提单回写（空=未提单）
  ticket_platform?: string
  ticket_no?: string
  ticket_url?: string
}

/** 报告列表（project_id 传当前项目 ID，实现项目隔离） */
export const listReports = (params: {
  keyword?: string
  page?: number
  page_size?: number
  task_type?: number
  run_result?: number
  run_mode?: number
  project_id?: number
}) => getPage<TestReport>('/test-reports', params)

/** 创建报告（执行完成后上报，含步骤执行记录） */
export const createReport = (data: {
  name: string
  task_type: number
  run_result: number
  run_mode: number
  total_steps: number
  success_steps: number
  duration_ms: number
  scenario_id?: number | null
  project_id?: number | null
  executor_name?: string
  summary?: string
  steps?: {
    step_name: string
    step_type: number
    status: number
    duration_ms: number
    depth: number
    result: string
  }[]
}) => post<TestReport>('/test-reports', data)

/** 报告详情 */
export const getReport = (id: number) => get<TestReport>(`/test-reports/${id}`)

/** 删除报告 */
export const deleteReport = (id: number) => del(`/test-reports/${id}`)

// ==================== 四期 K：报告只读分享 ====================

/** 开启分享（生成/复用 token），返回分享路径 */
export const enableShare = (id: number) =>
  post<{ token: string; path: string }>(`/test-reports/${id}/share`)

/** 关闭分享 */
export const disableShare = (id: number) => post(`/test-reports/${id}/share/disable`)

/** 分享报告只读访问（无需登录，token 鉴权） */
export const getSharedReport = (token: string) => get<TestReport>(`/test-reports/share/${token}`)

// ==================== 四期 L：AI 摘要 / 失败分析 / 一键提单 ====================

/** 生成报告 AI 摘要（结果缓存到报告 ai_summary 字段） */
export const generateAiSummary = (id: number) =>
  post<{ ai_summary: string; generated_at: string }>(`/test-reports/${id}/ai-summary`)

/** 失败分析：失败分类分布 + 失败步骤明细 */
export interface ReportAnalysis {
  distribution: Record<string, number>
  failed_steps: { step_id: number; step_name: string; error: string; category?: string }[]
}

/** 报告失败分析（仅失败报告有意义） */
export const getReportAnalysis = (id: number) => get<ReportAnalysis>(`/test-reports/${id}/analysis`)

/** 一键提单到禅道/Jira，返回平台单据信息 */
export const createTicket = (id: number) =>
  post<{
    message?: string
    ticket_key?: string
    ticket_url?: string
    provider?: string
    // 五期 #5：提单回写字段（与报告详情 ticket_* 一致）
    ticket_platform?: string
    ticket_no?: string
  }>(`/test-reports/${id}/create-ticket`)
